import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Sparkle, SquaresFour, DownloadSimple, FileJs, ImagesSquare, CaretDown, CaretUp,
} from "@/components/icons";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PanelCard from "@/components/PanelCard";
import VideoPanel from "@/components/VideoPanel";
import BottomNav from "@/components/BottomNav";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LAYOUTS = [
  { v: "storyboard", l: "Storyboard" },
  { v: "cinematic", l: "Cinematic" },
  { v: "2", l: "2 Panel" },
  { v: "4", l: "4 Panel" },
  { v: "6", l: "6 Panel" },
  { v: "9", l: "9 Panel" },
  { v: "manga", l: "Manga" },
  { v: "webtoon", l: "Webtoon" },
];
const STYLES = ["Cinematic", "Pixar 3D", "Anime / Manga", "Comic Book", "Noir", "Watercolor", "Photorealistic"];

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}

const GRID = {
  storyboard: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  cinematic: "grid-cols-1 max-w-4xl mx-auto",
  webtoon: "grid-cols-1 max-w-2xl mx-auto",
  "2": "grid-cols-1 sm:grid-cols-2",
  "4": "grid-cols-1 sm:grid-cols-2",
  "6": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  "9": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  manga: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
};

export default function Studio() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoRan = useRef(false);
  const [project, setProject] = useState(null);
  const [story, setStory] = useState("");
  const [style, setStyle] = useState("Cinematic");
  const [breaking, setBreaking] = useState(false);
  const [showStory, setShowStory] = useState(true);
  const [genAll, setGenAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setProject(data);
      setStory(data.story || "");
      setStyle(data.style || "Cinematic");
      setShowStory((data.panels || []).length === 0);
    } catch (e) {
      toast.error("Project not found");
      navigate("/dashboard");
    }
  }, [projectId, navigate]);

  useEffect(() => { load(); }, [load]);

  // auto-run breakdown when arriving from New Story (?auto=1)
  useEffect(() => {
    if (autoRan.current) return;
    if (project && searchParams.get("auto") === "1" && (project.panels || []).length === 0 && (project.story || "").trim()) {
      autoRan.current = true;
      searchParams.delete("auto");
      setSearchParams(searchParams, { replace: true });
      runBreakdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, searchParams]);

  const runBreakdown = async () => {
    if (!story.trim()) { toast.error("Write your story first"); return; }
    setBreaking(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/breakdown`, { story, style });
      setProject(data);
      setShowStory(false);
      toast.success(`${data.panels.length} panels created`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Breakdown failed");
    } finally { setBreaking(false); }
  };

  const changeSetting = async (patch) => {
    try {
      const { data } = await api.put(`/projects/${projectId}/settings`, patch);
      setProject(data);
    } catch { toast.error("Update failed"); }
  };

  const generateAll = async () => {
    if (!project?.panels?.length) return;
    setGenAll(true);
    const pending = project.panels.filter((p) => !p.image_base64);
    let ranOut = false;
    for (const panel of pending) {
      try {
        await api.post(`/projects/${projectId}/panels/${panel.id}/generate-image`);
      } catch (e) {
        if (e.response?.status === 402) { ranOut = true; break; }
      }
    }
    await load();
    await refreshUser();
    setGenAll(false);
    if (ranOut) { toast.error("Out of credits — buy more to finish rendering."); navigate("/pricing"); }
    else toast.success("Storyboard rendered");
  };

  const exportZip = async () => {
    try {
      const token = localStorage.getItem("sb_token");
      const res = await fetch(`${API}/projects/${projectId}/export/zip`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${project.title.replace(/\s+/g, "_")}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("ZIP export failed"); }
  };

  const oneClick = async () => {
    if (!story.trim()) { toast.error("Write your story first"); return; }
    setBreaking(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/breakdown`, { story, style });
      setProject(data);
      setShowStory(false);
      setBreaking(false);
      toast.success(`${data.panels.length} panels — now rendering images…`);
      setGenAll(true);
      let ranOut = false;
      for (const panel of data.panels) {
        try { await api.post(`/projects/${projectId}/panels/${panel.id}/generate-image`); }
        catch (e) { if (e.response?.status === 402) { ranOut = true; break; } }
      }
      await load();
      await refreshUser();
      setGenAll(false);
      if (ranOut) { toast.error("Out of credits — buy more to finish."); navigate("/pricing"); }
      else toast.success("One-Click storyboard complete 🎬");
    } catch (e) {
      setBreaking(false);
      setGenAll(false);
      toast.error(formatApiError(e.response?.data?.detail) || "One-Click failed");
    }
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(project, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${project.title.replace(/\s+/g, "_")}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to export PDF"); return; }
    const panelsHtml = (project.panels || []).map((p, i) => `
      <div class="panel">
        <h2>${i + 1}. ${escapeHtml(p.scene_title)}</h2>
        ${p.image_base64 ? `<img src="data:image/png;base64,${p.image_base64}" />` : `<div class="ph">No image generated</div>`}
        <p class="desc">${escapeHtml(p.description)}</p>
        <table>
          <tr><td>Camera</td><td>${escapeHtml(p.camera_angle)}</td></tr>
          <tr><td>Emotion</td><td>${escapeHtml(p.emotion)}</td></tr>
          <tr><td>Time / Weather</td><td>${escapeHtml(p.time_of_day)} · ${escapeHtml(p.weather)}</td></tr>
          <tr><td>Dialogue</td><td>${escapeHtml(p.dialogue || "-")}</td></tr>
          <tr><td>Animation</td><td>${escapeHtml(p.animation_notes)}</td></tr>
        </table>
      </div>`).join("");
    w.document.write(`<!doctype html><html><head><title>${escapeHtml(project.title)}</title>
      <style>
        body{font-family:Arial,sans-serif;background:#fff;color:#111;padding:32px;}
        h1{font-size:28px;border-bottom:3px solid #FACC15;padding-bottom:8px;}
        .panel{page-break-inside:avoid;margin:24px 0;border:1px solid #ddd;padding:16px;}
        .panel h2{font-size:18px;margin:0 0 10px;}
        .panel img{width:360px;max-width:100%;display:block;margin-bottom:10px;border:1px solid #eee;}
        .ph{width:360px;height:200px;background:#f3f3f3;display:flex;align-items:center;justify-content:center;color:#999;margin-bottom:10px;}
        .desc{font-size:14px;margin:0 0 10px;}
        table{font-size:12px;border-collapse:collapse;width:100%;}
        td{border:1px solid #eee;padding:6px 8px;vertical-align:top;}
        td:first-child{font-weight:bold;width:120px;text-transform:uppercase;font-size:10px;color:#666;}
      </style></head><body>
      <h1>${escapeHtml(project.title)} — Storyboard</h1>
      ${panelsHtml}
      <script>window.onload=()=>setTimeout(()=>window.print(),400);<\/script>
      </body></html>`);
    w.document.close();
  };

  const updatePanelInProject = (updatedProject) => setProject(updatedProject);

  if (!project) {
    return <div className="min-h-screen flex items-center justify-center bg-ink-950 text-white"><span className="label-mono animate-pulse">Loading studio…</span></div>;
  }

  const panels = project.panels || [];
  const pendingCount = panels.filter((p) => !p.image_base64).length;
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-ink-950 text-white">
      {/* top bar */}
      <header className="sticky top-0 z-50 bg-ink-950/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate("/creations")} className="text-zinc-400 hover:text-white shrink-0" data-testid="back-btn"><ArrowLeft size={20} /></button>
            <div className="min-w-0">
              <div className="label-mono">Studio</div>
              <h1 className="font-heading font-bold text-lg truncate leading-tight">{project.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {panels.length > 0 && (
              <>
                <button onClick={generateAll} disabled={genAll} data-testid="generate-all-btn" className="hidden sm:flex items-center gap-2 bg-ink-800 border border-zinc-700 text-sm px-3 py-2 hover:border-gold transition-colors disabled:opacity-50">
                  <ImagesSquare size={16} className="text-gold" /> {genAll ? "Rendering…" : "Render All"}
                </button>
                <button onClick={exportPDF} data-testid="export-pdf-btn" className="flex items-center gap-1.5 bg-ink-800 border border-zinc-700 text-sm px-3 py-2 hover:border-gold transition-colors"><DownloadSimple size={16} /> <span className="hidden sm:inline">PDF</span></button>
                <button onClick={exportZip} data-testid="export-zip-btn" className="flex items-center gap-1.5 bg-ink-800 border border-zinc-700 text-sm px-3 py-2 hover:border-gold transition-colors"><DownloadSimple size={16} /> <span className="hidden sm:inline">ZIP</span></button>
                <button onClick={exportJSON} data-testid="export-json-btn" className="flex items-center gap-1.5 bg-ink-800 border border-zinc-700 text-sm px-3 py-2 hover:border-gold transition-colors"><FileJs size={16} /> <span className="hidden sm:inline">JSON</span></button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* story / controls */}
        <div className="border border-zinc-800 bg-ink-900 mb-6">
          <button onClick={() => setShowStory(!showStory)} className="w-full flex items-center justify-between px-5 py-3 border-b border-zinc-800" data-testid="toggle-story">
            <span className="label-mono">Story Script</span>
            {showStory ? <CaretUp size={16} className="text-zinc-500" /> : <CaretDown size={16} className="text-zinc-500" />}
          </button>
          {showStory && (
            <div className="p-5 space-y-4">
              <textarea
                value={story}
                onChange={(e) => setStory(e.target.value)}
                rows={5}
                data-testid="studio-story-input"
                className="w-full bg-ink-950 border border-zinc-800 px-4 py-3 text-sm leading-relaxed focus:border-gold focus:outline-none resize-none"
                placeholder="Write your full story here…"
              />
              <div className="flex flex-wrap items-center gap-3">
                <div className="w-44">
                  <Select value={style} onValueChange={(v) => { setStyle(v); changeSetting({ style: v }); }}>
                    <SelectTrigger className="bg-ink-950 border-zinc-800 rounded-none focus:ring-gold" data-testid="style-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ink-900 border-zinc-800 text-white">
                      {STYLES.map((s) => <SelectItem key={s} value={s} className="focus:bg-ink-800 focus:text-gold">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <button onClick={runBreakdown} disabled={breaking || genAll} data-testid="breakdown-btn" className="flex items-center gap-2 bg-gold text-ink-950 font-heading font-bold px-6 py-2.5 hover:bg-goldlight transition-colors disabled:opacity-60">
                  <Sparkle weight="fill" size={18} /> {breaking ? "Directing…" : panels.length ? "Re-generate Storyboard" : "Generate Storyboard"}
                </button>
                <button onClick={oneClick} disabled={breaking || genAll} data-testid="one-click-btn" className="flex items-center gap-2 bg-[#1a140d] border border-gold/50 text-gold font-heading font-bold px-6 py-2.5 hover:bg-[#241a10] transition-colors disabled:opacity-60">
                  <Sparkle weight="fill" size={18} /> {genAll ? "Rendering all…" : "One-Click Animation"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* layout selector */}
        {panels.length > 0 && (
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <span className="label-mono">{panels.length} Panels</span>
              {pendingCount > 0 && (
                <span className="text-xs font-mono text-gold border border-gold/30 bg-[#1a140d] px-2.5 py-1" data-testid="cost-preview">
                  🎨 Render all {pendingCount} → {isAdmin ? "free" : `${pendingCount * 15} credits`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <SquaresFour size={16} className="text-gold" />
              <div className="w-40">
                <Select value={project.layout} onValueChange={(v) => changeSetting({ layout: v })}>
                  <SelectTrigger className="bg-ink-900 border-zinc-800 rounded-none focus:ring-gold h-9 text-sm" data-testid="layout-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ink-900 border-zinc-800 text-white">
                    {LAYOUTS.map((l) => <SelectItem key={l.v} value={l.v} className="focus:bg-ink-800 focus:text-gold text-sm">{l.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* panels */}
        {breaking && panels.length === 0 ? (
          <div className="border border-dashed border-zinc-800 py-24 flex flex-col items-center">
            <div className="w-40 h-0.5 tracing-border mb-5" />
            <p className="label-mono">The director is breaking down your story…</p>
          </div>
        ) : panels.length === 0 ? (
          <div className="border border-dashed border-zinc-800 py-24 flex flex-col items-center text-center" data-testid="empty-studio">
            <Sparkle size={40} weight="duotone" className="text-zinc-700 mb-4" />
            <p className="text-zinc-400">Write your story above and hit Generate Storyboard.</p>
          </div>
        ) : (
          <div className={`grid gap-5 ${GRID[project.layout] || GRID.storyboard}`} data-testid="panels-grid">
            {panels.map((panel, i) => (
              <PanelCard key={panel.id} panel={panel} index={i} projectId={projectId} onUpdate={updatePanelInProject} />
            ))}
          </div>
        )}

        <VideoPanel projectId={projectId} projectTitle={project.title} defaultPrompt={panels.find((p) => p.animation_notes)?.animation_notes || story} />
      </main>
      <BottomNav />
    </div>
  );
}
